<template>
  <div
    class="sizes"
    :class="{
      'sizes--inside-quick-add': isInsideQuickAdd,
      'sizes--inside-quick-view': isInsideQuickView,
      'sizes--error': hasError,
      'sizes--disabled': isDisabled,
      'sizes--show-size-info': hasSizeInfo,
    }"
    @mouseenter="preloadOverlayComponents"
  >
    <div
      v-if="isInsideQuickAdd || isInsideQuickView || hasSizeInfo"
      class="sizes__title--container"
    >
      <p class="sizes__title">
        {{
          hasSizeInfoShoes
            ? $t(`${gender}_sizes_shoes`, { sizeSystem })
            : $t(`${gender}_sizes`, { sizeSystem })
        }}
      </p>
    </div>
    <div class="sizes__wrapper">
      <div
        v-for="(size, index) in variantSkus"
        :key="index"
        class="sizes__size"
        :class="{
          'sizes__size--1': variantSkusCount === 1,
          'sizes__size--2': isVariantSock || isApparelInsideQuickView,
          'sizes__size--4': isInsideQuickView,
          'sizes__size--5': variantSkusCount === 5,
          'sizes__size--no-stock': size.stock === 0,
          'sizes__size--selected': size.sku === selectedSku,
        }"
        @click.prevent="
          handleSizeClick(
            { size: size.size, price: size.price, stock: size.stock, sku: size.sku },
            $event
          )
        "
        v-data-test-id="'size'"
      >
        {{ isGiftCard ? formatGiftCardPrices(size.price) : size.size }}
      </div>
      <span
        v-if="variantSkusCount > 1 && !isGiftCard && !isGenderUnisex"
        class="sizes__size sizes__size--info"
        :class="{
          'sizes__size--2': isVariantSock,
          'sizes__size--5': variantSkusCount === 5,
        }"
        @click="openSizeInfoModal"
        @mouseenter.once="loadFitIfNeeded"
        v-data-test-id="'size-guide'"
      >
        <IconRuler />
      </span>
      <transition name="fade">
        <div v-if="hasError" class="sizes__error">
          {{ !isGiftCard ? sizeErrorShoes : $t('select_price') }}
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { GENDERS, PRODUCT_TYPES } from '@/stores/product'
import client from '@/api/client'
import productFitQuery from '@/api/gql/product/query/product-fit.gql'
import { formatToCurrency, formatToWholeNumber } from '@/helpers/currency-formatter'
import IconRuler from '@/components/_icons/IconRuler.svg'

export const UNSELECTED_SKU = ''

export default {
  name: 'Sizes',
  components: {
    IconRuler,
  },

  props: {
    imageUrl: {
      type: String,
      required: true,
    },

    productName: {
      type: String,
      required: true,
    },

    gender: {
      type: String,
      validator: value => Object.keys(GENDERS).includes(value),
    },

    hasError: {
      type: Boolean,
      default: false,
    },

    isAutoselectedForOnesize: {
      type: Boolean,
      default: false,
    },

    isBackorderable: {
      type: Boolean,
      required: true,
    },

    isDisabled: {
      type: Boolean,
      default: false,
    },

    isInsideQuickAdd: {
      type: Boolean,
      default: false,
    },

    isInsideQuickView: {
      type: Boolean,
      default: false,
    },

    isRaffle: {
      type: Boolean,
      default: false,
    },

    isSizeReset: {
      type: Boolean,
      default: true,
    },

    resetSizeTimestamp: {
      type: Date,
    },

    sizeFinderButtonState: {
      type: Object,
      required: true,
    },

    variant: {
      type: Object,
      required: true,
    },

    isVariantSock: {
      type: Boolean,
      required: true,
    },

    variantSkus: {
      type: Array,
      required: true,
    },

    slug: {
      type: String,
      required: true,
    },

    hasSizeInfo: {
      type: Boolean,
    },

    isGiftCard: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      selectedSku: '',
      productFit: {},
    }
  },

  computed: {
    ...mapState('store', ['sizeSystem', 'currency']),
    ...mapState('page', ['locale']),

    isApparelInsideQuickView() {
      return this.isInsideQuickView && this.productType === PRODUCT_TYPES.apparel
    },

    isGenderUnisex() {
      return this.variant.gender === GENDERS.unisex
    },

    productType() {
      return this.variant.spreeProduct ? this.variant.spreeProduct.productType : ''
    },

    hasSizeInfoShoes() {
      return this.productType === PRODUCT_TYPES.shoes
    },

    sizeErrorShoes() {
      return this.productType === PRODUCT_TYPES.shoes
        ? this.$t('select_size_shoes')
        : this.$t('select_size')
    },

    variantSkusCount() {
      return this.variantSkus.length
    },
  },

  watch: {
    resetSizeTimestamp() {
      const selectedStock = this.variantSkus.find(sku => sku.sku === this.selectedSku)?.stock
      if (selectedStock === 0 || selectedStock === undefined) {
        this.selectedSku = ''
        this.$emit('select-size', {})
      }
    },

    isSizeReset() {
      if (this.isSizeReset) this.selectedSku = UNSELECTED_SKU
    },
  },

  created() {
    if (this.isOnesizeAutoselection && this.variantSkusCount === 1 && this.variantSkus[0].stock > 0)
      this.updateSelectedSize(this.variantSkus[0])
  },

  mounted() {
    if (On.isTouchDevice()) this.preloadOverlayComponents()
  },

  methods: {
    ...mapActions({
      showSizeGuideOverlay: 'overlay/showSizeGuideOverlay',
      showOutOfStockOverlay: 'overlay/showOutOfStockOverlay',
      preloadOutOfStockOverlay: 'overlay/preloadOutOfStockOverlay',
      preloadSizeGuideOverlay: 'overlay/preloadSizeGuideOverlay',
    }),

    loadFitIfNeeded() {
      if (!this.variant.fit && !this.productFit.sizeAndFit) this.fetchFitData()
    },

    async fetchFitData() {
      const {
        data: { product },
      } = await client.query({
        query: productFitQuery,
        variables: {
          sku: this.variant.spreeProduct.sku,
        },
      })
      this.productFit = product.productVariantData.fit
    },

    updateSelectedSize(selectedSkuObj, $event) {
      this.selectedSku = selectedSkuObj.sku
      this.$emit('select-size', selectedSkuObj, $event)
    },

    handleSizeClick(selectedSkuObj, $event) {
      if (this.isDisabled) return
      const { sku } = selectedSkuObj
      if (selectedSkuObj.stock <= 0) {
        this.showOutOfStockNotification(selectedSkuObj)
      } else {
        this.updateSelectedSize(selectedSkuObj, $event)
        const splitSku = sku.split(' ')

        this.$notifier.trackEvent({
          action: this.$notifier.ACTIONS.productInteraction,
          details: { action: 'size_select', label: `${splitSku[1]} ${splitSku[2]}` },
        })
      }
    },

    openSizeInfoModal() {
      this.showSizeGuideOverlay({
        fit: this.variant.fit || this.productFit,
        gender: this.findGenderInVariant(),
        imageUrl: this.imageUrl,
        isBackorderable: this.isBackorderable,
        productName: this.variant.productName || this.variant.spreeProduct.groupName,
        productType: this.variant.spreeProduct.productType,
        productUrl: this.variant.spreeProduct.productUrl || this.variant.productUrl,
        variantSkus: this.variantSkus,
        sizeFinderButtonState: this.sizeFinderButtonState,
      })

      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.sizeGuide,
        details: { action: 'open_overlay', label: 'info icon' },
      })

      if (this.productType === 'shoes') {
        this.$notifier.trackEvent({
          action: this.$notifier.ACTIONS.sizeGuide,
          details: { action: 'open_size_finder', label: 'info icon' },
        })
      }
    },

    findGenderInVariant() {
      const getValue = prop => (prop && Array.isArray(prop) ? prop[0] : prop)
      return getValue(this.variant.gender) || getValue(this.variant.filterValues?.gender)
    },

    preloadOverlayComponents() {
      this.preloadOutOfStockOverlay()
      this.preloadSizeGuideOverlay()
    },

    showOutOfStockNotification({ sku }) {
      this.showOutOfStockOverlay({
        sku,
        slug: this.slug,
        imageUrl: this.imageUrl,
        isBackorderable: this.isBackorderable,
        isRaffle: this.isRaffle,
      })
    },

    formatGiftCardPrices(amount) {
      const formattedPrice = formatToCurrency(amount, this.locale, this.currency)
      return formatToWholeNumber(formattedPrice)
    },
  },
}
</script>

<style lang="scss" scoped>
@import './Sizes';
</style>

<i18n src="@/locales/sizes.json"></i18n>
